import { type CSSProperties, useEffect, useState } from "react";

import {
  IconInternalUsers,
  IconPeople,
  IconUser,
} from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import "./BuiAvatar.less";

/**
 * create an array of display types so that it can be looped over and then
 * create the property type from this array
 */
export const buiAvatarColorize = [
  "default",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "indigo",
  "purple",
  "pink",
] as const;

export const buiAvatarSizes = ["xs", "sm", "md", "lg"] as const;

export const buiAvatarIconNames = ["User", "InternalUsers", "People"] as const;

/**
 * Bui Avatar display types created from array which is used for any looping
 */
export type TBuiAvatarColorize = (typeof buiAvatarColorize)[number];
export type TBuiAvatarSizes = (typeof buiAvatarSizes)[number];
export type TBuiAvatarIconNames = (typeof buiAvatarIconNames)[number];

export interface BuiAvatarProps {
  /**
   * Id applied to the rendered tag.
   */
  id?: string;
  /**
   * data-testid applied to the rendered tag for testing purposes.
   */
  testid?: string;
  /**
   * Provide the full name.
   * Full name will be converted to initials. This can handle multi name and hyphenated names.
   * @default undefined
   */
  text?: string;
  /**
   * Provide the src location of an image file
   * if you would like an image display
   * @default undefined
   */
  src?: string;
  /**
   * Provide the size of the Avatar display in pixels
   */
  // sizeInPx?: number; // Add if we find we need to allow for any size to be used
  /**
   * Provide the size of the Avatar display in pixels
   * @default "xs"
   */
  size?: TBuiAvatarSizes; // Add if we find we need to allow for any size to be used
  /**
   * Acts as a reference to the icon you want to use and is
   * crucial for rendering the correct icon.
   * @default "User"
   */
  iconName?: TBuiAvatarIconNames;
  /**
   * Select a defined colorized color to use
   * only for initials display.
   * Note legacy colors can still be passed with `style` values for background and color;
   * @default "default"
   */
  colorize?: TBuiAvatarColorize;
  /**
   * The className attribute for the BuiAvatar.
   * @default undefined
   */
  className?: string;
  /**
   * The style attribute for the BuiAvatar.
   * @default undefined
   */
  style?: CSSProperties;
}

const getAvatarIcon = (iconName: TBuiAvatarIconNames) => {
  switch (iconName) {
    case "InternalUsers":
      return <IconInternalUsers className="bui-avatar--icon" size="auto" />;
    case "People":
      return <IconPeople className="bui-avatar--icon" size="auto" />;
    case "User":
    default:
      return <IconUser className="bui-avatar--icon" size="auto" />;
  }
};

/**
 * BuiAvatar is the Blueprint primitive for displaying a user or entity avatar.
 * It resolves its content from the props in a fixed priority order:
 * - When `src` points to an image that loads successfully, the image is shown.
 * - Otherwise, when `text` is provided, initials derived from it are shown
 *   (first and last name, handling multi-word and hyphenated names).
 * - Otherwise, the `iconName` icon is shown as a fallback.
 *
 * `colorize` applies a BDS-defined background color and is only used for the
 * initials and icon fallbacks (it is ignored once an image is shown). For legacy
 * colors outside the `colorize` set, pass `background`/`color` via `style`.
 * @param props - The avatar component properties
 * @param props.id - Id applied to the rendered tag
 * @param props.testid - Test ID applied to the rendered tag (falls back to `id`)
 * @param props.text - Full name used to derive initials and as the image alt text
 * @param props.src - Image URL; takes precedence over initials once it loads
 * @param props.iconName - Fallback icon shown when there is no image or text (defaults to `User`)
 * @param props.size - Avatar size preset (defaults to `xs`)
 * @param props.colorize - BDS color applied to the initials/icon fallback (defaults to `default`)
 * @param props.className - Additional CSS classes to apply
 * @param props.style - Inline styles to apply
 * @example
 * ```tsx
 * // Image avatar (pass `text` too so it becomes the alt text)
 * <BuiAvatar src="/avatars/jane.png" text="Jane Doe" size="md" />
 *
 * // Initials avatar with a BDS color
 * <BuiAvatar text="Jane Doe" colorize="blue" />
 *
 * // Icon fallback (no text or src)
 * <BuiAvatar iconName="People" />
 * ```
 */
export function BuiAvatar({
  id,
  testid,
  text,
  src,
  iconName = "User",
  size = "xs",
  colorize = "default",
  className,
  style,
}: BuiAvatarProps) {
  const [isImage, setIsImage] = useState(false);
  const resolvedTestid = testid || id;

  useEffect(() => {
    // make sure we have a valid image url
    setIsImage(false); // reset state while loading new image
    if (src && src.length) {
      const image = new Image();
      const onSetIsImageLoad = () => setIsImage(true);
      const onSetIsImagError = () => setIsImage(false);
      image.addEventListener("load", onSetIsImageLoad);
      image.addEventListener("error", onSetIsImagError);
      image.src = src;
      return () => {
        image.removeEventListener("load", onSetIsImageLoad);
        image.removeEventListener("error", onSetIsImagError);
      };
    }
    return () => {};
  }, [src]);

  const getInitials = (textStr: string) => {
    const allNames = textStr
      .replace(/[!@#$%^&*(){}[\]~`"':;<>,.?/]/g, "")
      .trim()
      .split(" ");

    const initials = allNames.reduce((acc, curr, index) => {
      if (index === 0 || index === allNames.length - 1) {
        return `${acc}${curr.charAt(0)}`;
      }
      return acc;
    }, "");

    return initials;
  };

  return (
    <div
      id={id}
      data-testid={resolvedTestid}
      className={cn("bui-avatar", className, {
        [`bui-avatar-${colorize}`]: colorize && (!src || !isImage),
        [`bui-avatar-${size}`]: size,
      })}
      style={{ ...style }}
    >
      {src && isImage && (
        <img
          className="bui-avatar--image"
          src={src}
          alt={text || "user avatar"}
        />
      )}
      {(!src || (src && !isImage)) && text !== undefined && text !== "" && (
        <div className="bui-avatar--initials">{getInitials(text)}</div>
      )}
      {(!src || (src && !isImage)) && !text && getAvatarIcon(iconName)}
    </div>
  );
}
